<script setup lang="ts">
import { computed, onBeforeMount, ref, onBeforeUnmount, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useDashboardStore } from "@/modules/dashboards/store";
import DashboardCardDO = App.Data.DashboardCardDO;
import StoredDocumentDO = App.Data.StoredDocumentDO;
import DocumentAlertListViewDTO = App.Data.Alerts.DocumentAlertListViewDTO;
import { useApiClient } from "@/api/client";
import FileDTO = App.Data.FileDTO;
import SelectStoredDocumentDialog from "@/modules/dashboards/components/SelectStoredDocumentDialog.vue";
import { useBaseStore } from "@/stores/base";
import SecondaryDrawerHeader from "@/modules/explorer/components/SecondaryDrawer/SecondaryDrawerHeader.vue";
import Switch from "@/ui/components/switch/switch.vue";
import HelpTooltip from "@/ui/components/HelpTooltip.vue";
import { v4 as uuidv4 } from "uuid";
import { QuillEditor } from "@vueup/vue-quill";
import "@vueup/vue-quill/dist/vue-quill.snow.css";
import { promptInvalidPermissions } from "@/api/helpers/loadingWrapper";
import router from "@/router/router";
import useDialog from "@/ui/composables/useDialog";
import ColorPicker from "@/ui/components/colorPicker/ColorPicker.vue";
import LocaleNumberInput from "@/ui/components/LocaleNumberInput.vue";
import ValueFormat = App.Enums.ValueFormat;
import { processedDocument } from "chartjs-graph";

const createEmptyCard = (): DashboardCardDO => ({
	id: null,
	uuid: null,
	type: "graph",
	layout: { i: 0, x: 0, y: 0, w: 6, h: 12, moved: false },
	html_content: null,
	use_dashboard_date_settings: true,
	should_show_graph_configuration_text: false,
	stored_document: null,
	use_card_layout_height: true,
	render_as_card: true,
	title_align: "left",
	is_title_visible: true,
	use_static_title: false,
	static_title: null,
	visibility_alert_id: null,
	visibility_condition: "always",
	target: null,
	value_color: "#1976D2",
	range_color: "#32B955",
	progress_style: "bar",
	target_stored_document: null,
	target_percentage: 100,
	target_is_range: false,
	target_min: null,
	target_max: null,
	cached_data: null,
	total_row: null,
	image_url: null,
	image_width: null,
	image_height: null,
});

const { t } = useI18n();

const componentState = ref<"create" | "edit">("create");
const dashboardState = useDashboardStore().dashboardState;

const card = ref<DashboardCardDO>(createEmptyCard());

const availableAlerts = ref<DocumentAlertListViewDTO[]>([]);

const loadAlerts = async () => {
	const result = await useApiClient("alerts").get({
		page: 1,
		itemsPerPage: 1000,
		sortBy: [],
	});
	if (result.isOk()) {
		availableAlerts.value = result.value.data;
	}
};

const visibilityConditionItems = [
	{ title: "Always", value: "always" },
	{ title: "Only when alert is firing", value: "when_in_alert" },
	{ title: "Only when alert is not firing", value: "when_not_in_alert" },
];

const alertItems = computed(() =>
	availableAlerts.value.map((alert) => ({
		title: alert.stored_document?.title ?? `Alert #${alert.id}`,
		subtitle: `${t(`properties.${alert.properties.metric_name}.title`)} · ${t(
			`alerts.check_types.${alert.check_interval_type}`
		)}`,
		value: alert.id,
	}))
);

// Clear the selected alert when conditional rendering is disabled so the card
// does not persist a dangling alert reference.
watch(
	() => card.value.visibility_condition,
	(condition) => {
		if (condition === "always") {
			card.value.visibility_alert_id = null;
		}
	}
);

const selectedFile = ref<FileDTO>();
const isLoadingSelectedDocumentFileUuid = ref("");
const storedDocuments = ref<StoredDocumentDO[]>([]);
const isSelectStoredDocumentDialogVisible = ref(false);
const isLoading = ref(true);

const hideActiveFilterLabels = computed({
	get: () => !card.value.should_show_graph_configuration_text,
	set: (value: boolean) => {
		card.value.should_show_graph_configuration_text = !value;
	},
});
const scaleCardHeightToContent = computed({
	get: () => !card.value.use_card_layout_height,
	set: (value: boolean) => {
		card.value.use_card_layout_height = !value;
	},
});

const target_mode: Ref<"value" | "metric"> = ref("value");
const targetSelectedFile: Ref<FileDTO> = ref();
const isSelectTargetDocumentDialogVisible = ref(false);
const isLoadingTargetDocumentFileUuid = ref("");
const valueFormat: Ref<ValueFormat> = ref("default");

const editorOptions = {
	theme: "snow",
	modules: {
		toolbar: [
			[{ header: [1, 2, 3, false] }],
			[{ color: [] }, { background: [] }],
			["bold", "italic", "underline", "strike"],
			[{ list: "ordered" }, { list: "bullet" }],
			[{ align: [] }],
			["blockquote"],
		],
	},
};

watch(selectedFile, async () => {
	valueFormat.value = "default";
	if (!selectedFile.value) {
		isLoadingSelectedDocumentFileUuid.value = "";
		card.value.stored_document = null;

		return;
	}

	isLoadingSelectedDocumentFileUuid.value = selectedFile.value.uuid;

	const response = await promptInvalidPermissions(
		useApiClient("documents").openStoredDocument(selectedFile.value)
	);
	if (isLoadingSelectedDocumentFileUuid.value !== selectedFile.value.uuid) {
		// Make sure the request is coming in for the most recent selection.
		return;
	}

	isLoadingSelectedDocumentFileUuid.value = "";
	if (response.isOk()) {
		card.value.stored_document = {
			...selectedFile.value,
			activeDocument: response.value.replicated_document,
			active_document_uuid: response.value.original_document_uuid,
		};

		const documentViewTab = response.value.replicated_document.view_tab;

		if (
			!(card.value.type === "progress" && documentViewTab === "single-metric")
		) {
			card.value.type = documentViewTab;
		}

		if (card.value.type === "progress") {
			const fileUuid = selectedFile.value.uuid;
			const metricResponse = await promptInvalidPermissions(
				useApiClient("explorer").fetchDataForDocumentSingleMetric(
					processedDocument(response.value.replicated_document),
					"explorer/"
				)
			);
			if (selectedFile.value?.uuid === fileUuid && metricResponse.isOk()) {
				valueFormat.value = metricResponse.value.singleMetric.format;
			}
		}
	} else {
		card.value.stored_document = null;
	}
});

watch(targetSelectedFile, async () => {
	if (!targetSelectedFile.value) {
		isLoadingTargetDocumentFileUuid.value = "";
		card.value.target_stored_document = null;

		return;
	}

	isLoadingTargetDocumentFileUuid.value = targetSelectedFile.value.uuid;

	const response = await promptInvalidPermissions(
		useApiClient("documents").openStoredDocument(targetSelectedFile.value)
	);
	if (isLoadingTargetDocumentFileUuid.value !== targetSelectedFile.value.uuid) {
		// Make sure the request is coming in for the most recent selection.
		return;
	}

	if (response.isOk()) {
		card.value.target_stored_document = {
			...targetSelectedFile.value,
			activeDocument: response.value.replicated_document,
			active_document_uuid: response.value.original_document_uuid,
		};
	} else {
		card.value.target_stored_document = null;
	}
	isLoadingTargetDocumentFileUuid.value = "";
});

async function addCardToDashboard() {
	if (componentState.value === "create") {
		addNewCardToDashboard();
	} else {
		await updateExistingCardInDashboard();
	}
	selectedFile.value = undefined;
	closeDrawer();
}

const updateExistingCardInDashboard = async () => {
	const cards = dashboardState.dashboard.cards;
	let index = cards.findIndex((c) => c.uuid === dashboardState.cardToEdit.uuid);

	if (index === -1) {
		index = cards.findIndex((c) => c.id === dashboardState.cardToEdit.id);
	}
	if (index === -1) return;

	// a text-only card has no document
	if (!card.value.stored_document && card.value.type !== "text-only") {
		return;
	}

	const updatedCard: DashboardCardDO = {
		...card.value,
		id: null,
		uuid: uuidv4(),
	};

	cards.splice(index, 1);
	cards.push(updatedCard);
};

const addNewCardToDashboard = () => {
	const cards = dashboardState.dashboard.cards;

	const maxId = Math.max(1, ...cards.map((c) => c.layout?.i || 0));
	const maxY = Math.max(-1, ...cards.map((c) => c.layout?.y || 0));

	const isProgress = card.value.type === "progress";
	const isLine = card.value.progress_style === "bar";
	const dimensions = isProgress
		? isLine
			? { w: 6, h: 4 }
			: { w: 4, h: 5 }
		: { w: 6, h: 12 };

	const newCard: DashboardCardDO = {
		...card.value,
		id: null,
		uuid: uuidv4(),
		layout: { x: 0, y: maxY + 1, ...dimensions, i: maxId + 1, moved: false },
	};

	cards.push(newCard);
};

async function fetchStoredDocuments() {
	const response = await useApiClient(
		"documents"
	).getAllStoredDocumentsForUser();
	if (response.isOk()) {
		storedDocuments.value = response.value;
		isLoading.value = false;
	}
}

const displayTitle = computed({
	get() {
		return selectedFile.value?.title ?? "";
	},
	set() {},
});

const selectStoredDocument = (file: FileDTO) => {
	selectedFile.value = file;
};

const targetDisplayTitle = computed({
	get() {
		return targetSelectedFile.value?.title ?? "";
	},
	set() {},
});

const selectTargetDocument = (file: FileDTO) => {
	targetSelectedFile.value = file;
};

const showSelectTargetDocument = (value) => {
	if (value) {
		isSelectTargetDocumentDialogVisible.value = true;
	}
};

const openExplorer = async (slot: "document" | "target" = "document") => {
	const confirm = await useDialog().confirm({
		title: "Open Explorer",
		message:
			"This will redirect you to the explorer, where you can edit the newly created document, save it and return back to the dashboard.",
	});

	if (!confirm) return;

	dashboardState.pendingNewCard = {
		card: { ...card.value },
		slot,
		documentUuid: null,
	};

	const routeName =
		card.value.type === "graph"
			? "explorer.graphWrapper"
			: "explorer.metricWrapper";

	router.push({
		name: routeName,
		query: {
			dashboard: dashboardState.dashboard.uuid,
			fresh: "true",
		},
	});
};

const showSelectStoredDocument = (value: boolean) => {
	if (value) {
		isSelectStoredDocumentDialogVisible.value = true;
	}
};

const closeDrawer = () => {
	useBaseStore().showSecondaryDrawer = false;
};

onBeforeMount(async () => {
	await Promise.all([fetchStoredDocuments(), loadAlerts()]);
	initState();
});

const invalidMaxAndMin = computed(() => {
	if (!card.value.target_is_range) return false;

	const min = card.value.target_min;
	const max = card.value.target_max;

	return (
		min !== null &&
		min !== undefined &&
		max !== null &&
		max !== undefined &&
		max < min
	);
});

const findStoredDocumentByUuid = (uuid?: string) =>
	uuid ? storedDocuments.value.find((doc) => doc.uuid === uuid) : undefined;

const initState = () => {
	const pendingNewCard = dashboardState.pendingNewCard;
	if (pendingNewCard) {
		dashboardState.pendingNewCard = null;

		const { card: stashedCard, slot, documentUuid } = pendingNewCard;

		if (stashedCard) {
			card.value = { ...stashedCard };
			componentState.value = dashboardState.cardToEdit ? "edit" : "create";
			target_mode.value =
				slot === "target" || card.value.target_stored_document
					? "metric"
					: "value";
		}

		const storedDocumentUuid = stashedCard?.stored_document?.uuid;
		const targetDocumentUuid = stashedCard?.target_stored_document?.uuid;

		selectedFile.value = findStoredDocumentByUuid(
			slot === "document"
				? documentUuid ?? storedDocumentUuid
				: storedDocumentUuid
		);
		targetSelectedFile.value = findStoredDocumentByUuid(
			slot === "target"
				? documentUuid ?? targetDocumentUuid
				: targetDocumentUuid
		);
		return;
	}

	const cardToEdit = dashboardState.cardToEdit;

	if (!cardToEdit) {
		selectedFile.value = undefined;
		return;
	}

	componentState.value = "edit";
	card.value = { ...cardToEdit };
	target_mode.value = cardToEdit.target_stored_document ? "metric" : "value";

	selectedFile.value = storedDocuments.value.find(
		(doc) => doc.id === cardToEdit.stored_document?.id
	);
	targetSelectedFile.value = storedDocuments.value.find(
		(doc) => doc.id === cardToEdit.target_stored_document?.id
	);
};

watch(
	() => card.value.type,
	(v) => {
		useBaseStore().secondaryDrawerWidth = 400;
		if (v === "text-only") {
			useBaseStore().secondaryDrawerWidth = 600;
		}
	}
);

watch(
	() => card.value.type,
	(v) => {
		if (v === "text-only") {
			card.value.is_title_visible = false;
		}
	}
);

onBeforeUnmount(() => {
	dashboardState.cardToEdit = null;
	useBaseStore().secondaryDrawerWidth = 400;
});

const insertTag = (tag: string) => {
	card.value.html_content = (card.value.html_content ?? "") + tag;
};

const cardTypeItems = [
	{
		title: "Graph",
		value: "graph",
	},
	{
		title: "Single Metric",
		value: "single-metric",
	},
	{
		title: "Table",
		value: "table",
	},
	{
		title: "Text Only",
		value: "text-only",
	},
	{
		title: "Progress",
		value: "progress",
	},
];

const tags = [
	"{period_from}",
	"{period_to}",
	"{dashboard_filters}",
	"{dashboard_recipients}",
	"{ai_rapport}",
];
</script>

<template>
	<select-stored-document-dialog
		v-if="isSelectStoredDocumentDialogVisible"
		v-model="isSelectStoredDocumentDialogVisible"
		:fetch-documents="fetchStoredDocuments"
		:allow-create-new="true"
		@file-selected="selectStoredDocument"
		@open-explorer="openExplorer('document')"
	/>
	<select-stored-document-dialog
		v-if="isSelectTargetDocumentDialogVisible"
		v-model="isSelectTargetDocumentDialogVisible"
		:fetch-documents="fetchStoredDocuments"
		:allow-create-new="true"
		@file-selected="selectTargetDocument"
		@open-explorer="openExplorer('target')"
	/>
	<div class="d-flex flex-column h-100">
		<div class="flex-grow-0">
			<SecondaryDrawerHeader
				icon="fa: fa-duotone fa-grid-2-plus"
				title="Card Configuration"
			/>
		</div>

		<v-progress-linear indeterminate v-if="isLoading" height="2" />
		<v-fade-transition>
			<div v-if="!isLoading">
				<div class="d-flex flex-column ga-9 pa-4">
					<div>
						<div class="d-flex align-center ga-2 mb-2">
							<div class="font-weight-semibold text-no-wrap">General</div>
							<v-divider class="flex-grow-1" />
						</div>

						<v-label>Card type</v-label>
						<v-select
							variant="outlined"
							density="compact"
							hide-details
							v-model="card.type"
							style="text-transform: capitalize"
							:items="cardTypeItems"
						/>
						<div class="mt-3" v-if="card.type !== 'text-only'">
							<v-label>Explorer configuration</v-label>
							<v-text-field
								@update:focused="showSelectStoredDocument"
								v-model="displayTitle"
								prepend-inner-icon="fa-regular fa-file"
								hide-details
								variant="outlined"
								density="compact"
								placeholder="Select a document"
							/>
						</div>
					</div>
					<div v-if="card.type === 'text-only'">
						<div class="d-flex align-center ga-2 mb-2">
							<div class="font-weight-semibold text-no-wrap">Content</div>
							<v-divider class="flex-grow-1" />
						</div>
						<v-label> Text </v-label>
						<div class="rounded-editor">
							<quill-editor
								v-model:content="card.html_content"
								content-type="html"
								theme="snow"
								:options="editorOptions"
							/>
						</div>
						<p class="text-caption my-3">
							You can use tags in your text that will be dynamically replaced
							when rendered. Currently, the following tags are available:
						</p>
						<div class="d-flex flex-wrap ga-2">
							<v-chip
								v-for="tag in tags"
								:key="tag"
								label
								size="small"
								color="secondary-tonal"
								@click="insertTag(tag)"
							>
								{{ tag }}
							</v-chip>
						</div>
					</div>
					<div v-if="card.type === 'progress'">
						<div class="d-flex align-center ga-2 mb-2">
							<div class="font-weight-semibold text-no-wrap">
								Progress Configuration
							</div>
							<v-divider class="flex-grow-1" />
						</div>
						<v-label>Style</v-label>
						<div class="d-flex align-center ga-3 mb-3 pc-style-row">
							<v-btn-toggle
								v-model="card.progress_style"
								class="pc-segment"
								variant="outlined"
								density="compact"
								mandatory
								divided
							>
								<v-btn value="bar">Bar</v-btn>
								<v-btn value="circle">Circle</v-btn>
								<v-btn value="gauge">Gauge</v-btn>
							</v-btn-toggle>
							<div class="pc-color-wrap">
								<color-picker v-model="card.value_color" :height="36" />
							</div>
						</div>

						<div class="d-flex ga-1">
							<v-label>Target </v-label>
							<help-tooltip>
								The value is the single metric of the configuration selected
								above. Set the target that represents 100%
							</help-tooltip>
						</div>
						<v-btn-toggle
							v-model="target_mode"
							variant="outlined"
							density="compact"
							mandatory
							divided
							class="pc-segment mb-3"
						>
							<v-btn value="value">Fixed value</v-btn>
							<v-btn value="metric">From configuration</v-btn>
						</v-btn-toggle>

						<div class="mb-3" v-if="target_mode === 'metric'">
							<v-label>Target Explorer configuration</v-label>
							<v-text-field
								@update:focused="showSelectTargetDocument"
								v-model="targetDisplayTitle"
								prepend-inner-icon="fa-regular fa-file"
								hide-details
								variant="outlined"
								density="compact"
								placeholder="Select a document"
							/>
						</div>

						<v-label>Target type </v-label>
						<v-btn-toggle
							v-model="card.target_is_range"
							variant="outlined"
							density="compact"
							mandatory
							divided
							class="pc-segment mb-3"
						>
							<v-btn :value="false">Single</v-btn>
							<v-btn :value="true">Range</v-btn>
						</v-btn-toggle>

						<template v-if="!card.target_is_range">
							<v-label>Target value </v-label>
							<locale-number-input
								v-if="target_mode === 'value'"
								v-model="card.target"
								:format="valueFormat"
								allow-empty
								variant="outlined"
								density="compact"
								class="mb-3"
								hide-details
							/>
							<div v-else class="d-flex align-center mb-4">
								<v-number-input
									v-model="card.target_percentage"
									density="compact"
									hide-details
									control-variant="hidden"
									variant="outlined"
									suffix="%"
									:min="0"
									:precision="null"
								/>
							</div>
						</template>

						<div v-else>
							<v-label> Range </v-label>
							<div class="d-flex ga-3 align-center">
								<div v-if="target_mode === 'value'" class="d-flex ga-3 w-100">
									<locale-number-input
										v-model="card.target_min"
										:format="valueFormat"
										allow-empty
										label="Min"
										:error="invalidMaxAndMin"
										variant="outlined"
										density="compact"
										hide-details
										class="flex-grow-1"
									/>
									<locale-number-input
										v-model="card.target_max"
										:format="valueFormat"
										allow-empty
										label="Max"
										:error="invalidMaxAndMin"
										variant="outlined"
										density="compact"
										hide-details
										class="flex-grow-1"
									/>
								</div>
								<div v-else class="d-flex ga-3 w-100">
									<v-number-input
										v-model="card.target_min"
										placeholder="Min"
										suffix="%"
										:error="invalidMaxAndMin"
										density="compact"
										variant="outlined"
										hide-details
										control-variant="hidden"
										:min="0"
										:precision="null"
										class="flex-grow-1"
									/>
									<v-number-input
										v-model="card.target_max"
										placeholder="Max"
										suffix="%"
										:error="invalidMaxAndMin"
										density="compact"
										variant="outlined"
										hide-details
										control-variant="hidden"
										:min="0"
										:precision="null"
										class="flex-grow-1"
									/>
								</div>
								<div class="pc-color-wrap">
									<color-picker v-model="card.range_color" :height="40" />
								</div>
							</div>
							<div v-if="invalidMaxAndMin" class="text-caption text-error mt-1">
								Max can't be lower than min.
							</div>
						</div>
					</div>
					<div v-if="card.type !== 'text-only'">
						<div class="d-flex align-center ga-2 mb-2">
							<div class="font-weight-semibold text-no-wrap">Date Settings</div>
							<v-divider class="flex-grow-1" />
						</div>
						<div class="d-flex">
							<div class="d-flex ga-1 w-auto">
								<v-label>Use dashboard's dates</v-label>
								<help-tooltip>
									The selected configuration has its own date selection. By
									default, when used in a dashboard, it is overridden by the
									dashboard's date selection to keep all cards synchronized. To
									use the configuration’s original date selection instead,
									disable the switch on the right.
								</help-tooltip>
							</div>
							<v-spacer />
							<div>
								<Switch v-model="card.use_dashboard_date_settings" />
							</div>
						</div>
					</div>

					<div>
						<div class="d-flex align-center ga-2 mb-2">
							<div class="font-weight-semibold text-no-wrap">
								Conditional Rendering
							</div>
							<v-divider class="flex-grow-1" />
						</div>
						<div class="d-flex ga-1">
							<v-label>Render this card in exports</v-label>
							<help-tooltip>
								Choose whether this card should be rendered when this dashboard
								is exported. When rendering conditionally, the state of the
								alert is only read, not recalculated. If the alert has a
								different schedule than the trigger for this dashboard, the
								alert can be out of date.
							</help-tooltip>
						</div>
						<v-select
							v-model="card.visibility_condition"
							variant="outlined"
							density="compact"
							hide-details="auto"
							:items="visibilityConditionItems"
						/>
						<div class="mt-3" v-if="card.visibility_condition !== 'always'">
							<v-label>Alert</v-label>
							<v-autocomplete
								v-model="card.visibility_alert_id"
								variant="outlined"
								density="compact"
								:menu-props="{ class: 'alert-select-menu' }"
								hide-details="auto"
								placeholder="Select an alert"
								:items="alertItems"
								:rules="[
									(v) =>
										v != null || 'Select an alert for conditional rendering',
								]"
							>
								<template #item="{ props: itemProps, item }">
									<v-list-item
										v-bind="itemProps"
										:subtitle="item.raw.subtitle"
									/>
								</template>
							</v-autocomplete>
						</div>
					</div>

					<div>
						<div class="d-flex align-center ga-2 mb-2">
							<div class="font-weight-semibold text-no-wrap">
								Other Settings
							</div>
							<v-divider class="flex-grow-1" />
						</div>
						<div class="d-flex mb-2" v-if="card.type !== 'text-only'">
							<v-label>Hide active filter labels</v-label>
							<v-spacer />
							<div>
								<Switch v-model="hideActiveFilterLabels" />
							</div>
						</div>
						<div class="d-flex mb-2" v-if="card.type === 'table'">
							<v-label>Scale card height to content</v-label>
							<v-spacer />
							<div>
								<Switch v-model="scaleCardHeightToContent" />
							</div>
						</div>
						<div class="d-flex mb-2" v-if="card.type !== 'text-only'">
							<v-label>Show title on card</v-label>
							<v-spacer />
							<div>
								<Switch v-model="card.is_title_visible" />
							</div>
						</div>
						<v-expand-transition>
							<div class="pl-5" v-if="card.type !== 'text-only'">
								<div class="d-flex mb-2" v-if="card.is_title_visible">
									<v-label>Use static title</v-label>
									<v-spacer />
									<div>
										<Switch v-model="card.use_static_title" />
									</div>
								</div>
								<div class="d-flex mb-2" v-if="card.is_title_visible">
									<v-label class="pt-1">Align title</v-label>
									<v-spacer />
									<div>
										<v-btn-toggle
											v-model="card.title_align"
											variant="outlined"
											density="compact"
										>
											<v-btn value="left">
												<v-icon>mdi-format-align-left</v-icon>
											</v-btn>
											<v-btn value="center">
												<v-icon>mdi-format-align-center</v-icon>
											</v-btn>
											<v-btn value="right">
												<v-icon>mdi-format-align-right</v-icon>
											</v-btn>
										</v-btn-toggle>
									</div>
								</div>
								<div
									class="d-flex mb-2 w-100"
									v-if="card.is_title_visible && card.use_static_title"
								>
									<div class="w-100">
										<v-label>Title</v-label>
										<v-text-field
											placeholder="Provide Title"
											class="w-100"
											v-model="card.static_title"
											variant="outlined"
											density="compact"
										/>
									</div>
								</div>
							</div>
						</v-expand-transition>
						<div class="d-flex">
							<v-label>Render as card</v-label>
							<v-spacer />
							<div>
								<Switch v-model="card.render_as_card" />
							</div>
						</div>
					</div>
				</div>
				<v-divider />
				<div class="d-flex pa-2">
					<v-spacer />
					<v-btn
						color="primary"
						:disabled="
							(!selectedFile &&
								card.type !== 'text-only' &&
								!card.stored_document) ||
							(card.type === 'progress' &&
								target_mode === 'metric' &&
								!card.target_stored_document) ||
							invalidMaxAndMin
						"
						elevation="0"
						@click="addCardToDashboard"
					>
						{{ componentState === "create" ? "Create" : "Update" }} Card
					</v-btn>
				</div>
			</div>
		</v-fade-transition>
	</div>
</template>

<style scoped lang="scss">
.alert-select-menu .v-list-item-title {
	font-size: 0.875rem;
}

.rounded-editor :deep(.ql-toolbar) {
	border-top-left-radius: 4px !important;
	border-top-right-radius: 4px !important;
}
.rounded-editor :deep(.ql-container) {
	border-bottom-left-radius: 4px !important;
	border-bottom-right-radius: 4px !important;
}

.pc-segment {
	width: 100%;

	:deep(.v-btn) {
		flex: 1 1 0;
		text-transform: none;
		letter-spacing: normal;
		font-weight: 500;
	}
}

.pc-pct {
	gap: 0.5rem;

	&__input {
		flex: 0 0 auto;
		width: 120px;
	}
}

.pc-color-wrap {
	flex: 0 0 auto;
}
</style>
